import { useEffect, useSyncExternalStore } from 'react';

/**
 * ROBO-932 — the record-specific part of the browser tab title ("123 Main St · Cobra Kai").
 *
 * A module store, not context: the page that knows the record sits deep inside the router, and
 * `RouteTitleAndPageView`, which writes `document.title`, sits beside the router. One page sets a
 * detail at a time.
 *
 * ★ TAB ONLY. This text is personal data — an address, a client's name — and never goes to
 * analytics. `RouteTitleAndPageView` sends the route's static title instead.
 */
let currentDetail: string | undefined;
const listeners = new Set<() => void>();

function setDetail(next: string | undefined) {
    if (next === currentDetail) return;
    currentDetail = next;
    listeners.forEach(listener => listener());
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Call from a record page with the record's display text; pass undefined while it loads. */
export function usePageTitleDetail(detail: string | undefined) {
    const trimmed = detail?.trim() || undefined;
    useEffect(() => {
        setDetail(trimmed);
        return () => setDetail(undefined);
    }, [trimmed]);
}

/** A property's tab text: the street line, with the unit line when there is one. */
export function addressTitle(location: { address?: string; address2?: string } | undefined) {
    return [location?.address, location?.address2]
        .map(part => part?.trim())
        .filter(Boolean)
        .join(' ') || undefined;
}

export function usePageTitleDetailValue(): string | undefined {
    return useSyncExternalStore(subscribe, () => currentDetail);
}
